import { z } from 'zod'
import { WORKFLOW_FILE_REMOVE_BATCH_SIZE } from './workflowFileManagement'

/**
 * Validates one file or folder name as a single provider path segment.
 *
 * Rejects traversal, separators, and characters SharePoint reserves, without
 * restricting extensions or MIME types.
 */
export const WorkflowFileNameSchema = z
  .string()
  .min(1)
  .max(255)
  .refine(
    (name) =>
      name !== '.' &&
      name !== '..' &&
      !/["*:<>?\\/|\u0000-\u001f]/.test(name) &&
      !/^ |[ .]$/.test(name),
    'Use a file or folder name without traversal, separators, or reserved characters'
  )

/**
 * Validates the shape of a provider item ID.
 *
 * An ID only identifies the caller's intent; the server separately verifies
 * the item's drive and ancestry.
 */
export const WorkflowFileIdSchema = z
  .string()
  .min(1)
  .max(255)
  .refine(
    (id) => id !== '.' && id !== '..' && !/[\\/?#\u0000-\u001f]/.test(id),
    'Expected an item ID, not a path or URL'
  )

/** Request body for creating, or reusing, a folder beneath a parent folder. */
export const WorkflowFileFolderSchema = z
  .object({
    parentId: WorkflowFileIdSchema,
    name: WorkflowFileNameSchema,
  })
  .strict()

/** Request body for starting one file upload into a parent folder. */
export const WorkflowFileUploadSchema = WorkflowFileFolderSchema.extend({
  size: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
}).strict()

/** Request body for removing one batch of files and folders. */
export const WorkflowFileRemoveSchema = z
  .object({
    ids: z
      .array(WorkflowFileIdSchema)
      .min(1)
      .max(WORKFLOW_FILE_REMOVE_BATCH_SIZE),
  })
  .strict()
